import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Image, Users, Mail,
  Newspaper, LogOut, Inbox, Heart, Settings, Globe,
} from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { adminApi } from '../components/admin/api';
import { DashboardOverview } from '../components/admin/DashboardOverview';
import { BlogTab } from '../components/admin/BlogTab';
import { TeamTab } from '../components/admin/TeamTab';
import { GalleryTab } from '../components/admin/GalleryTab';
import { ProgramsTab } from '../components/admin/ProgramsTab';
import { MessagesTab } from '../components/admin/MessagesTab';
import { NewsletterTab } from '../components/admin/NewsletterTab';
import { DonationsTab } from '../components/admin/DonationsTab';
import { SettingsPanel } from '../components/admin/SettingsPanel';

const TABS = [
  { id: 'dashboard', label: 'Apercu', icon: LayoutDashboard },
  { id: 'blog', label: 'Actualites', icon: FileText },
  { id: 'programs', label: 'Programmes', icon: Newspaper },
  { id: 'gallery', label: 'Galerie', icon: Image },
  { id: 'team', label: 'Equipe', icon: Users },
  { id: 'contacts', label: 'Messages', icon: Mail },
  { id: 'newsletter', label: 'Newsletter', icon: Inbox },
  { id: 'donations', label: 'Dons', icon: Heart },
  { id: 'settings', label: 'Reglages', icon: Settings },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [blog, setBlog] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [donations, setDonations] = useState([]);

  const verify = useCallback(async () => {
    try {
      await adminApi.get(API_ENDPOINTS.adminVerify);
    } catch (err) {
      console.error('Auth verification failed:', err);
      sessionStorage.clear();
      navigate('/admin/login');
    }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try {
      setStats((await adminApi.get(API_ENDPOINTS.adminStats)).data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadTab = useCallback(async (t) => {
    try {
      if (t === 'blog') setBlog((await adminApi.get(API_ENDPOINTS.adminBlog)).data);
      else if (t === 'programs') setPrograms((await axios.get(API_ENDPOINTS.programs)).data);
      else if (t === 'gallery') setGallery((await axios.get(API_ENDPOINTS.gallery)).data);
      else if (t === 'team') setTeam((await adminApi.get(API_ENDPOINTS.adminTeam)).data);
      else if (t === 'contacts') setContacts((await adminApi.get(API_ENDPOINTS.adminContacts)).data);
      else if (t === 'newsletter') setNewsletter((await adminApi.get(API_ENDPOINTS.adminNewsletter)).data);
      else if (t === 'donations') {
        try {
          setDonations((await adminApi.get(API_ENDPOINTS.adminDonationsList)).data);
        } catch {
          setDonations([]);
        }
      }
    } catch (err) {
      console.error(`Failed to load ${t}:`, err);
    }
  }, []);

  useEffect(() => { verify(); loadStats(); }, [verify, loadStats]);
  useEffect(() => { if (tab !== 'dashboard' && tab !== 'settings') loadTab(tab); }, [tab, loadTab]);

  const logout = () => { sessionStorage.clear(); navigate('/admin/login'); };

  const handleQuickAction = (target) => {
    setTab(target);
  };

  const renderTab = () => {
    switch (tab) {
      case 'dashboard':
        return <DashboardOverview stats={stats} onTabChange={setTab} onQuickAction={handleQuickAction} />;
      case 'blog':
        return <BlogTab blog={blog} onReload={() => loadTab('blog')} onReloadStats={loadStats} />;
      case 'programs':
        return <ProgramsTab programs={programs} onReload={() => loadTab('programs')} />;
      case 'gallery':
        return <GalleryTab gallery={gallery} onReload={() => loadTab('gallery')} onReloadStats={loadStats} />;
      case 'team':
        return <TeamTab team={team} onReload={() => loadTab('team')} onReloadStats={loadStats} />;
      case 'contacts':
        return <MessagesTab contacts={contacts} onReload={() => loadTab('contacts')} onReloadStats={loadStats} />;
      case 'newsletter':
        return <NewsletterTab newsletter={newsletter} />;
      case 'donations':
        return <DonationsTab donations={donations} />;
      case 'settings':
        return <SettingsPanel />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* TOP BAR */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 h-16">
        <div className="h-full max-w-[1440px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/images/logo-hpd.png" alt="HPD" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-gray-900 text-sm tracking-tight">HPD</span>
            <span className="text-gray-300">|</span>
            <span className="text-xs text-gray-500">Administration</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1 transition-colors">
              <Globe className="w-3.5 h-3.5" />Voir le site
            </a>
            <div className="w-px h-5 bg-gray-200" />
            <span className="text-xs text-gray-500" data-testid="admin-username">{sessionStorage.getItem('adminUser') || 'Admin'}</span>
            <button onClick={logout} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* NAV TABS */}
      <nav className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-1 scrollbar-hide">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
                  tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
                {t.id === 'contacts' && stats?.unread_contacts > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {stats.unread_contacts}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* CONTENT */}
      <main className="pt-[120px] max-w-[1440px] mx-auto px-6 pb-12">
        {renderTab()}
      </main>
    </div>
  );
};

export default AdminDashboard;
